"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { UserPlus, X, Send, Save, Plus, AlertTriangle, Shield, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";

import {
  formatPhoneNumber,
  toE164Format,
} from "@/app/_module/utils/formatters";
import { handlePhoneKeyDown } from "@/app/_module/utils/phoneInputUtils";
import { A2PGateNotice } from "@/app/_module/components/A2PGateNotice";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";

import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { a2pService } from "@/app/_module/services/a2pService";

import { useUserContext } from "@/app/_module/store/contexts/userContext";

import { createErsSessionSchema, type CreateErsSessionSchema } from "../schema/ers-schema";
import {
  useCreateErsSession,
  useUpdateErsSession,
  useSendErsMessages,
  useErsTemplates,
} from "../hooks/ers-queries";
import type { ErsSession, ErsTemplate } from "../types/ers.types";

const RELATIONSHIP_OPTIONS = [
  "Spouse",
  "Child",
  "Parent",
  "Sibling",
  "Grandchild",
  "Grandparent",
  "Partner",
  "Friend",
  "Coworker",
  "Neighbor",
  "Other",
];

const DEFAULT_TEMPLATE = `Hi {{contact_name}}, this is {{agent_name}}. I just helped {{lead_name}} get their life insurance taken care of and they mentioned you might want to look into protecting your family too. Would it be okay if I gave you a quick call sometime this week?`;

interface ErsCollectModalProps {
  open: boolean;
  onClose: () => void;
  leadId: string;
  leadName: string;
  policyId?: string;
  /** If provided, the modal opens in edit mode pre-filled with this session's data. */
  session?: ErsSession;
}

export function ErsCollectModal({
  open,
  onClose,
  leadId,
  leadName,
  policyId,
  session,
}: ErsCollectModalProps) {
  const isEditMode = !!session;
  const { user } = useUserContext();
  const agentName = user?.user?.name || "[Your Name]";
  const [messageTemplate, setMessageTemplate] = useState(DEFAULT_TEMPLATE);
  const [a2pStatus, setA2pStatus] = useState<{ canSend: boolean; reason?: string } | null>(null);
  const [checkingA2P, setCheckingA2P] = useState(true);
  const createSession = useCreateErsSession();
  const updateSession = useUpdateErsSession();
  const sendMessages = useSendErsMessages();
  const { data: templates } = useErsTemplates();

  const form = useForm<CreateErsSessionSchema>({
    resolver: zodResolver(createErsSessionSchema),
    defaultValues: {
      leadId,
      policyId: policyId || undefined,
      contacts: [{ name: "", phone: "", relationship: undefined }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "contacts",
  });

  // Reset form and check A2P when modal opens or editing session changes
  useEffect(() => {
    if (open) {
      if (isEditMode && session?.contacts?.length) {
        form.reset({
          leadId,
          policyId: policyId || undefined,
          contacts: session.contacts.map((c) => ({
            name: c.name,
            // stored as E.164 (+1xxxxxxxxxx) — convert back to display format
            phone: formatPhoneNumber(c.phone),
            relationship: c.relationship || undefined,
          })),
        });
        setMessageTemplate(session.contacts[0]?.messageTemplate || DEFAULT_TEMPLATE);
      } else {
        form.reset({
          leadId,
          policyId: policyId || undefined,
          contacts: [{ name: "", phone: "", relationship: undefined }],
        });
        setMessageTemplate(DEFAULT_TEMPLATE);
      }

      setCheckingA2P(true);
      a2pService.canSendSMS()
        .then(setA2pStatus)
        .catch(() => setA2pStatus({ canSend: false, reason: "Failed to check A2P verification status" }))
        .finally(() => setCheckingA2P(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId, policyId, session?.id]);

  const handleTemplateSelect = (templateId: string) => {
    const template = (templates as ErsTemplate[])?.find((t) => t.id === templateId);
    if (template) setMessageTemplate(template.content);
  };

  const toE164Contacts = (contacts: CreateErsSessionSchema["contacts"]) =>
    contacts.map((c) => ({ ...c, phone: toE164Format(c.phone) }));

  const handleSaveDraft = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    const contactsE164 = toE164Contacts(values.contacts);

    if (isEditMode && session) {
      await updateSession.mutateAsync({
        sessionId: session.id,
        leadId,
        data: { contacts: contactsE164, messageTemplate },
      });
    } else {
      await createSession.mutateAsync({
        ...values,
        contacts: contactsE164,
        messageTemplate,
      });
    }
    onClose();
  };

  const handleSaveAndSend = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    const contactsE164 = toE164Contacts(values.contacts);

    try {
      let sessionId: string | undefined;

      if (isEditMode && session) {
        const result: any = await updateSession.mutateAsync({
          sessionId: session.id,
          leadId,
          data: { contacts: contactsE164, messageTemplate },
        });
        sessionId = result?.id ?? session.id;
      } else {
        const result: any = await createSession.mutateAsync({
          ...values,
          contacts: contactsE164,
          messageTemplate,
        });
        sessionId = result?.id;
      }

      if (sessionId) {
        await sendMessages.mutateAsync({ sessionId, leadId });
      }
      onClose();
    } catch {
      // Error toast handled by mutation
    }
  };

  const isSubmitting =
    createSession.isPending || updateSession.isPending || sendMessages.isPending;

  const previewMessage = messageTemplate
    .replace(/\{\{contact_name\}\}/gi, fields[0]?.name || "[Contact Name]")
    .replace(/\{\{agent_name\}\}/gi, agentName)
    .replace(/\{\{lead_name\}\}/gi, leadName?.split(" ")[0] || "[Client]")
    .replace(/\{\{relationship\}\}/gi, "your friend")
    .replace(/\{\{product_sold\}\}/gi, "life insurance")
    .replace(/\{\{agency_name\}\}/gi, "[Agency]");

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <ResponsiveDialogContent size="lg" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode ? (
              <Pencil className="h-5 w-5 text-primary" />
            ) : (
              <UserPlus className="h-5 w-5 text-primary" />
            )}
            {isEditMode ? `Edit Draft — ${leadName}` : `Collect Referrals from ${leadName}`}
          </DialogTitle>
          <DialogDescription className="space-y-1">
            <span className="block">
              {isEditMode
                ? "Update the contacts list. Changes replace the previous draft."
                : "Enter family and friends who might be interested in life insurance. They\u2019ll receive a personalized text from your number."}
            </span>
            <span className="block text-xs text-primary/70 font-medium">
              ✦ Powered by InsuraBot AI
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Contact Rows */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">
                Contacts ({fields.length}/10)
              </Label>
              {fields.length < 10 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    append({ name: "", phone: "", relationship: undefined })
                  }
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Contact
                </Button>
              )}
            </div>

            {fields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-[1fr_1fr_140px_32px] gap-2 items-start"
              >
                <div>
                  <Input
                    placeholder="Name"
                    {...form.register(`contacts.${index}.name`)}
                    className={
                      form.formState.errors.contacts?.[index]?.name
                        ? "border-red-500"
                        : ""
                    }
                  />
                  {form.formState.errors.contacts?.[index]?.name && (
                    <p className="text-xs text-red-500 mt-0.5">
                      {form.formState.errors.contacts[index].name?.message}
                    </p>
                  )}
                </div>
                <div>
                  <Controller
                    control={form.control}
                    name={`contacts.${index}.phone`}
                    render={({ field }) => (
                      <Input
                        placeholder="(555) 123-4567"
                        type="tel"
                        maxLength={14}
                        value={field.value ? formatPhoneNumber(field.value) : ""}
                        onChange={(e) => field.onChange(formatPhoneNumber(e.target.value))}
                        onKeyDown={handlePhoneKeyDown}
                        className={
                          form.formState.errors.contacts?.[index]?.phone
                            ? "border-red-500"
                            : ""
                        }
                      />
                    )}
                  />
                  {form.formState.errors.contacts?.[index]?.phone && (
                    <p className="text-xs text-red-500 mt-0.5">
                      {form.formState.errors.contacts[index].phone?.message}
                    </p>
                  )}
                </div>
                <Select
                  value={form.watch(`contacts.${index}.relationship`) || ""}
                  onValueChange={(val) =>
                    form.setValue(`contacts.${index}.relationship`, val || undefined)
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIP_OPTIONS.map((rel) => (
                      <SelectItem key={rel} value={rel}>
                        {rel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-8 text-gray-400 hover:text-red-500"
                    onClick={() => remove(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                {fields.length <= 1 && <div className="w-8" />}
              </div>
            ))}

            {form.formState.errors.contacts?.message && (
              <p className="text-xs text-red-500">
                {form.formState.errors.contacts.message}
              </p>
            )}
          </div>

          {/* Message Template */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Message Template</Label>
              {templates && (templates as ErsTemplate[])?.length > 0 && (
                <Select onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="w-48 h-8 text-xs">
                    <SelectValue placeholder="Load template" />
                  </SelectTrigger>
                  <SelectContent>
                    {(templates as ErsTemplate[]).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Textarea
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              rows={4}
              className="text-sm"
              placeholder="Enter your referral message..."
            />
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span className={messageTemplate.length > 160 ? "text-amber-600 font-medium" : ""}>
                {messageTemplate.length} chars{messageTemplate.length > 160 ? " — may send as 2+ SMS" : ""}
              </span>
              <span>
                Variables: {"{{contact_name}}"}, {"{{agent_name}}"}, {"{{lead_name}}"}
              </span>
            </div>
          </div>

          {/* Live Preview */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Preview</Label>
            <div className="bg-gray-50 border rounded-md p-3 text-sm text-gray-700 italic">
              {previewMessage}
            </div>
          </div>

          {/* A2P Warning */}
          {!checkingA2P && !a2pStatus?.canSend && (
            <A2PGateNotice
              stage={(a2pStatus as any)?.stage || 'required'}
              reason={a2pStatus?.reason}
              onAction={() => (window.location.href = "/settings/a2p-verification")}
            />
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <DialogClose asChild>
            <Button variant="outline" disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isSubmitting}
          >
            <Save className="h-4 w-4 mr-1" />
            {isEditMode ? "Update Draft" : "Save Draft"}
          </Button>
          <Button
            onClick={handleSaveAndSend}
            disabled={isSubmitting || checkingA2P || !a2pStatus?.canSend}
            title={!a2pStatus?.canSend ? "A2P verification required to send SMS" : undefined}
          >
            <Send className="h-4 w-4 mr-1" />
            {isSubmitting ? "Sending..." : isEditMode ? "Update & Send" : "Save & Send"}
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </Dialog>
  );
}
